(() => {
  const instances = new WeakMap();

  function initCollectionTabs(root) {
    if (!root || instances.has(root)) return;

    const tabbar = root.querySelector('.arch-coll__tabbar');
    const tablist = root.querySelector('[role="tablist"]');
    const tabs = Array.from(root.querySelectorAll('[data-mw-collection-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-mw-collection-panel]'));
    const collectionLink = root.querySelector('[data-mw-collection-link]');

    if (!tabbar || !tablist || tabs.length !== panels.length || !tabs.length) return;

    const controller = new AbortController();
    const signal = controller.signal;
    let frameId = 0;
    let activeIndex = Math.max(0, tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));

    function positionIndicator() {
      const tab = tabs[activeIndex];
      const label = tab && tab.querySelector('.arch-coll__tab-label');
      if (!tab || !label) return;

      const listRect = tablist.getBoundingClientRect();
      const labelRect = label.getBoundingClientRect();
      tabbar.style.setProperty('--mw-tab-x', `${labelRect.left - listRect.left}px`);
      tabbar.style.setProperty('--mw-tab-width', `${labelRect.width}px`);
    }

    function refreshActiveRail(panel) {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
        const viewport = panel.querySelector('[data-mw-rail-viewport]');
        if (viewport) viewport.dispatchEvent(new Event('scroll'));
      });
    }

    function activate(nextIndex, moveFocus = false) {
      if (nextIndex < 0 || nextIndex >= tabs.length) return;
      activeIndex = nextIndex;

      tabs.forEach((tab, index) => {
        const selected = index === activeIndex;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
      });

      panels.forEach((panel, index) => {
        panel.hidden = index !== activeIndex;
      });

      const activeTab = tabs[activeIndex];
      const activePanel = panels[activeIndex];
      const nextUrl = activeTab.dataset.collectionUrl;

      if (collectionLink && nextUrl) collectionLink.href = nextUrl;
      if (moveFocus) activeTab.focus({ preventScroll: true });

      positionIndicator();
      refreshActiveRail(activePanel);
    }

    function onClick(event) {
      const tab = event.target.closest('[data-mw-collection-tab]');
      const index = tabs.indexOf(tab);
      if (index !== -1) activate(index);
    }

    function onKeydown(event) {
      const currentIndex = tabs.indexOf(event.target.closest('[data-mw-collection-tab]'));
      if (currentIndex === -1) return;

      let nextIndex = currentIndex;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = tabs.length - 1;
      else return;

      event.preventDefault();
      activate(nextIndex, true);
    }

    tablist.addEventListener('click', onClick, { signal });
    tablist.addEventListener('keydown', onKeydown, { signal });

    const resizeObserver = new ResizeObserver(positionIndicator);
    resizeObserver.observe(tablist);

    activate(activeIndex);
    window.requestAnimationFrame(() => tabbar.classList.add('is-ready'));

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(positionIndicator);
    }

    instances.set(root, {
      destroy() {
        window.cancelAnimationFrame(frameId);
        resizeObserver.disconnect();
        controller.abort();
      }
    });
  }

  function initWithin(scope) {
    if (!scope) return;
    if (scope.matches && scope.matches('[data-mw-collection-tabs]')) initCollectionTabs(scope);
    scope.querySelectorAll?.('[data-mw-collection-tabs]').forEach(initCollectionTabs);
  }

  function destroyWithin(scope) {
    if (!scope) return;
    const roots = [];
    if (scope.matches && scope.matches('[data-mw-collection-tabs]')) roots.push(scope);
    scope.querySelectorAll?.('[data-mw-collection-tabs]').forEach((root) => roots.push(root));
    roots.forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWithin(document), { once: true });
  } else {
    initWithin(document);
  }

  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
